<script setup lang="ts">
import type { MenuItem } from 'primevue/menuitem'
import type { RouteParamsGeneric } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '@/shared/stores/AuthStore'
import { useAdminAccessStore } from '@/shared/stores/AdminAccessStore'
import { useViteEnvironment } from '@/shared/composables/useViteEnvironment'
import { resolveDefaultDepartment } from '@/shared/composables/UseMenuPreferences'
import MenuSettings from '@/features/dashboard/components/MenuSettings.vue'

const props = withDefaults(defineProps<{
  items: MenuItem[]
  /**
   * Icons-only rail mode (used between the mobile and desktop breakpoints). The rail keeps its
   * narrow width in the layout flow and expands over the content on hover/focus.
   */
  collapsed?: boolean
}>(), {
  collapsed: false,
})

const { isDev, isTest } = useViteEnvironment()
const router = useRouter()
const route = useRoute()
const userAuth = useAuthStore()
const { user } = storeToRefs(userAuth)
const adminAccess = useAdminAccessStore()

const initials = computed(() => {
  const parts = (user.value?.name ?? '').trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts.at(-1)![0]).toUpperCase()

  return (user.value?.name ?? '').slice(0, 2).toUpperCase()
})

const firstName = computed(() => (user.value?.name ?? '').trim().split(/\s+/)[0] ?? '')
const department = computed(() => resolveDefaultDepartment(adminAccess.access?.roles ?? []))

/*
 * Deliberately not router-link's `isActive`: the generated route tree groups every feature page
 * under a component-less parent (e.g. '/companies') whose index child shares the parent's path.
 * Vue-router then marks that parent active for ALL its children, so "Bedrijven" would light up on
 * /werkgevers-accounts too. Instead: same route name (covers aliases) or a real path prefix.
 */
const isItemActive = (item: MenuItem): boolean => {
  const target = router.resolve(item.route)

  if (target.name && route.name === target.name) {
    // Parameterized routes share a name (e.g. every dashboard detail): params must match too.
    const currentParams: RouteParamsGeneric = route.params

    return Object.entries(target.params).every(([ key, value ]) => String(currentParams[key]) === String(value))
  }

  return route.path === target.path || route.path.startsWith(`${target.path}/`)
}

const SIDEBAR_PIN_KEY = 'sidebar-pinned-collapsed'
const pinnedCollapsed = ref(localStorage.getItem(SIDEBAR_PIN_KEY) === 'true')

watch(pinnedCollapsed, val => localStorage.setItem(SIDEBAR_PIN_KEY, String(val)))

const hovered = ref(false)
const isCollapsed = computed(() => props.collapsed || pinnedCollapsed.value)
const expanded = computed(() => {
  if (pinnedCollapsed.value) return false
  if (!props.collapsed) return true

  return hovered.value
})

/** Loose entries (e.g. Homepage) render before the grouped subcategory sections. */
const looseItems = computed(() => props.items.filter(item => !item.items))
const groups = computed(() => props.items.filter(item => item.items?.length))

/*
 * Labels get a short fade so they appear once the width transition is mostly done (and disappear
 * instantly when collapsing, before they would get clipped mid-letter).
 */
const labelClass = computed(() => expanded.value ? 'opacity-100 delay-75' : 'opacity-0')
</script>

<template>
  <!-- The aside reserves layout width; the inner nav overlays the content when the rail expands. -->
  <aside class="relative h-100vh max-w-55 shrink-0" :class="isCollapsed ? 'w-16' : 'w-55'">
    <nav
      id="sidebar-nav"
      class="absolute inset-y-0 left-0 z-20 max-w-55 flex flex-col overflow-hidden border-r border-surface-100 bg-surface-0 transition-width duration-200 ease-out"
      :class="[expanded ? 'w-55' : 'w-16', isCollapsed && expanded ? 'shadow-xl' : '']"
      @mouseenter="!pinnedCollapsed && (hovered = true)"
      @mouseleave="!pinnedCollapsed && (hovered = false)"
      @focusin="!pinnedCollapsed && (hovered = true)"
      @focusout="!pinnedCollapsed && (hovered = false)"
    >
      <!-- Brand: the navy S4W badge is the rail icon; the wordmark joins it when expanded. -->
      <div class="h-16 flex shrink-0 items-center gap-x-3 px-3">
        <span class="relative h-10 w-10 flex shrink-0 items-center justify-center rounded-lg bg-primary text-sm text-surface-0 font-bold">
          S4W
          <span
            v-if="(isDev || isTest) && !expanded"
            class="absolute h-3 w-3 border-2 border-surface-0 rounded-full bg-danger -right-1 -top-1"
            :title="isDev ? 'DEV' : 'TEST'"
          />
        </span>
        <div class="min-w-0 flex flex-1 items-center gap-x-2 transition-opacity duration-150" :class="labelClass">
          <div class="min-w-0 flex flex-col leading-tight">
            <span class="whitespace-nowrap text-base color-primary font-bold">Swipe4Work</span>
            <span class="whitespace-nowrap text-xs color-surface-400 font-semibold tracking-wide uppercase">Admin</span>
          </div>
          <Tag
            v-if="isDev || isTest"
            severity="danger" :value="isDev ? 'DEV' : 'TEST'" class="ml-auto p-1" :pt="{
              label: {
                class: '!text-xs font-bold',
              },
            }"
          />
        </div>
      </div>

      <!-- Items -->
      <div class="sidebar-scroll min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-3 pb-2">
        <template v-for="section in [{ label: null, items: looseItems }, ...groups.map(g => ({ label: g.label as string, items: g.items as MenuItem[] }))]" :key="section.label ?? 'loose'">
          <!-- Group header: label when expanded, a centered divider in rail mode. -->
          <div v-if="section.label" class="relative mb-1 mt-3 h-5">
            <span
              class="absolute inset-y-0 left-2 flex items-center whitespace-nowrap text-xs color-surface-400 font-semibold tracking-wide uppercase transition-opacity duration-150"
              :class="labelClass"
            >
              {{ section.label }}
            </span>
            <span
              class="absolute left-1 top-1/2 w-8 border-t border-surface-200 transition-opacity duration-150"
              :class="expanded ? 'opacity-0' : 'opacity-100'"
              aria-hidden="true"
            />
          </div>

          <template v-for="item in section.items" :key="item.label">
            <router-link v-if="item.route" v-slot="{ href, navigate }" :to="item.route" custom>
              <a
                v-ripple
                v-tooltip.right="{ value: item.label, disabled: !pinnedCollapsed, showDelay: 100 }"
                :href="href"
                class="relative mb-0.5 flex items-center overflow-hidden rounded-lg no-underline transition-colors duration-100"
                :class="isItemActive(item)
                  ? 'bg-primary-50 color-primary font-semibold'
                  : 'color-surface-700 hover:bg-surface-50 hover:color-primary'"
                @click="(event) => {
                  if (event.ctrlKey || event.metaKey) return;
                  event.preventDefault();
                  navigate();
                }"
              >
                <span class="h-10 w-10 flex shrink-0 items-center justify-center text-lg">
                  <i :class="item.icon" />
                </span>
                <span class="line-clamp-2 min-w-0 flex-1 break-words pr-2 text-sm font-medium leading-tight transition-opacity duration-150" :class="labelClass" :title="item.label as string">
                  {{ item.label }}
                </span>
              </a>
            </router-link>
            <a
              v-else
              v-ripple
              v-tooltip.right="{ value: item.label, disabled: !pinnedCollapsed, showDelay: 100 }"
              :href="item.url"
              :target="item.target"
              class="relative mb-0.5 flex items-center overflow-hidden rounded-lg color-surface-700 no-underline transition-colors duration-100 hover:bg-surface-50 hover:color-primary"
            >
              <span class="h-10 w-10 flex shrink-0 items-center justify-center text-lg">
                <i :class="item.icon" />
              </span>
              <span class="line-clamp-2 min-w-0 flex-1 break-words pr-2 text-sm font-medium leading-tight transition-opacity duration-150" :class="labelClass" :title="item.label as string">
                {{ item.label }}
              </span>
            </a>
          </template>
        </template>
      </div>

      <!-- Footer: initials avatar (logout) always in rail; collapse + settings visible when expanded. -->
      <div class="shrink-0 border-t border-surface-100 p-3">
        <div class="flex items-center gap-x-1">
          <!-- Initials avatar doubles as logout button; tooltip always visible on hover -->
          <button
            v-tooltip.right="'Uitloggen'"
            type="button"
            class="h-10 w-10 flex shrink-0 cursor-pointer select-none items-center justify-center rounded-full border-none bg-primary text-sm text-surface-0 font-bold transition-opacity hover:opacity-80"
            aria-label="Uitloggen"
            @click="userAuth.logout().then(() => router.push({ name: 'auth.login' }))"
          >
            {{ initials }}
          </button>

          <!-- First name + department: fade in with expanded, clipped in rail -->
          <div class="min-w-0 flex flex-1 flex-col gap-y-0 pl-2 transition-opacity duration-150" :class="labelClass">
            <span class="truncate text-sm color-primary font-semibold">{{ firstName }}</span>
            <span v-if="department" class="truncate text-xs color-surface-400">{{ department }}</span>
          </div>

          <!-- Collapse toggle (left of settings): both fade with expanded, clipped by overflow-hidden in rail -->
          <div class="flex shrink-0 items-center transition-opacity duration-150" :class="labelClass">
            <Button
              v-tooltip="{ value: pinnedCollapsed ? 'Navigatie uitklappen' : 'Navigatie inklappen' }"
              type="button"
              text
              rounded
              severity="secondary"
              :icon="`fa-regular ${pinnedCollapsed ? 'fa-angles-right' : 'fa-angles-left'}`"
              :aria-label="pinnedCollapsed ? 'Navigatie uitklappen' : 'Navigatie inklappen'"
              @click="pinnedCollapsed = !pinnedCollapsed"
            />
            <MenuSettings compact />
          </div>
        </div>

        <!-- Rail-only collapse row: always accessible within the 40px rail content area. -->
        <div
          class="overflow-hidden transition-all duration-200 ease-out"
          :style="expanded ? 'max-height: 0; opacity: 0; margin-top: 0' : 'max-height: 2.25rem; opacity: 1; margin-top: 0.5rem'"
        >
          <button
            v-tooltip.right="{ value: pinnedCollapsed ? 'Navigatie uitklappen' : 'Navigatie inklappen' }"
            type="button"
            class="h-9 w-full flex cursor-pointer items-center rounded-lg border-none bg-transparent px-1 color-surface-400 transition-colors hover:bg-surface-100 hover:color-surface-700"
            :aria-label="pinnedCollapsed ? 'Navigatie uitklappen' : 'Navigatie inklappen'"
            @click="pinnedCollapsed = !pinnedCollapsed"
          >
            <span class="h-8 w-8 flex shrink-0 items-center justify-center text-base">
              <i class="fa-regular" :class="pinnedCollapsed ? 'fa-angles-right' : 'fa-angles-left'" />
            </span>
          </button>
        </div>
      </div>
    </nav>
  </aside>
</template>

<style scoped>
/* The rail is too narrow for a visible scrollbar; the list still scrolls with wheel/touch. */
.sidebar-scroll {
  scrollbar-width: none;
}
.sidebar-scroll::-webkit-scrollbar {
  display: none;
}
</style>
